import React, { useState } from "react";
import { css } from "@emotion/react";
import { useRouter } from "next/router";
import { stringify } from "qs";
import { decamelizeKeys } from "humps";
import { useTranslation } from "react-i18next";
import { HotelShowParam } from "main/javascripts/types/hotelShowParam";
import { fetchHotelPreReservation } from "main/javascripts/api/HotelPreReservationApi";
import {
  getDynamicPackageNextPage,
  putDynamicPackagePreReservation,
} from "main/javascripts/api/DynamicPackagePreReservationApi";
import { Amenity } from "main/javascripts/models/Amenity";
import { ButtonWithLoader } from "main/javascripts/components/button/ButtonWithLoader";
import { SocialMediaTypes } from "main/javascripts/constants/SocialMediaTypes";
import { sendLiffTextMessages } from "main/javascripts/api/LiffApi";
// import { beginShareFlowToCurrentThread } from "main/javascripts/api/FacebookMessengerApi";
import { postSelectHotel } from "main/javascripts/api/SelectHotelApi";
import { SearchTypes } from "main/javascripts/constants/SearchTypes";
import { stopBodyScrolling } from "main/javascripts/utils/ScrollUtil";
import {
  accentColor,
  backgroundColor,
  borderColor,
  textColor,
} from "main/javascripts/styles/base/colorStyle";
import { space } from "main/javascripts/styles/base/spaceStyle";
import { useAppSelector } from "main/javascripts/store";
import { getResearchHotelLiffId } from "main/javascripts/selectors/AccountParamsSelector";
import { moreThanBreakpoint } from "main/javascripts/styles/base/responsiveStyle";
import {
  fontSize,
  fontWeight,
  lineHeight,
} from "main/javascripts/styles/base/typographyStyle";
import { Hotel } from "main/javascripts/types/hotel";
import { PlatformTypes } from "main/javascripts/constants/PlatformTypes";
import { dpPreReservationEvent } from "main/javascripts/utils/googleTagManagerUtil";
import { currentUserSelector } from "main/javascripts/slices/currentUserSlice";

export interface IProps {
  hotelId: number;
  hotelRoomId: number | string;
  hotelPlan: Hotel.Plan;
  param: HotelShowParam;
  onSelectHotelPlan?: any;
  loggedInSocialMedia?: string;
}

export const HotelPlanContent: React.FC<IProps> = (
  props: IProps
): React.ReactElement => {
  const {
    hotelId,
    hotelRoomId,
    hotelPlan,
    param,
    onSelectHotelPlan,
    loggedInSocialMedia,
  } = props;
  const { t } = useTranslation(["label"]);
  const researchHotelLiffId = useAppSelector(getResearchHotelLiffId);
  const currentUser = useAppSelector(currentUserSelector);

  const showRoomNumCount = 5;

  const router = useRouter();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [hasCostMessage, setHasCostMessage] = useState(false);

  const onSelect: () => void = () => {
    if (onSelectHotelPlan) {
      onSelectHotelPlan(hasCostMessage);
    }
  };

  const onSelectError: (err: string | number) => void = (
    err: string | number
  ) => {
    setErrorText(String(err));
  };

  const onClickSelection: () => Promise<void> = async () => {
    setIsSubmitting(true);
    setErrorText("");
    setHasCostMessage(false);
    try {
      const { data } = await postSelectHotel({
        room_id: hotelRoomId,
        plan_id: hotelPlan.id,
        room_cache_id: param.roomCacheId,
        cache_id: param.cacheId,
        l_id: param.lId,
        q_c_id: param.qCId,
      });

      // 外部ブラウザでもLIFF.initできるので先にメールユーザーをチェックする
      if (currentUser?.chatPlatformType === PlatformTypes.email) {
        onSelect();
        setHasCostMessage(true);
      } else if (loggedInSocialMedia === SocialMediaTypes.liff) {
        if (data.messageText && data.messageText.length > 1) {
          setHasCostMessage(true);
        }
        sendLiffTextMessages(
          data.messageText,
          researchHotelLiffId,
          onSelect,
          onSelectError
        );
      } else if (loggedInSocialMedia === SocialMediaTypes.facebookMessenger) {
        // 現在FB未使用
        // const element: any = [
        //   {
        //     title: data.messageText[0],
        //     buttons: [
        //       {
        //         type: "web_url",
        //         url: "https://tabi-two.com/",
        //         title: "hotel information",
        //       },
        //     ],
        //   },
        // ];
        // TODO: beginShareFlowが使えなくなったため、ユーザーにメッセージを送信できない
        // TODO: メッセージ送信方法を別途検討必要
        // beginShareFlowToCurrentThread(element, this.onSelect, this.onSelectError);
        // onSelect();
      }

      setIsSubmitting(false);
    } catch (e) {
      console.log(e);
      setIsSubmitting(false);
      setErrorText(e.errorMessages);
    }
  };

  const onClickPreReservation: () => Promise<void> = async () => {
    setIsSubmitting(true);
    setErrorText("");
    try {
      // TODO: 旧API
      const { data } =
        param.searchType === SearchTypes.dynamicPackage
          ? await putDynamicPackagePreReservation(
              param.dynamicPackagePreReservationId,
              {
                hotel_pre_reservations: [
                  {
                    hotel_id: hotelId,
                    room_id: hotelRoomId,
                    plan_id: hotelPlan.id,
                    room_cache_id: param.roomCacheId,
                    cache_id: param.cacheId,
                    dynamic_package_id: param.dynamicPackageId,
                    search_group_id: param.searchGroupId,
                  },
                ],
              }
            )
          : await fetchHotelPreReservation({
              hotel_id: hotelId,
              room_id: hotelRoomId,
              plan_id: hotelPlan.id,
              room_cache_id: param.roomCacheId,
              cache_id: param.cacheId,
              dynamic_package_id: param.dynamicPackageId,
              search_group_id: param.searchGroupId,
            });
      if (param.dynamicPackageId) {
        // TODO: 旧API
        const { nextData } = await getDynamicPackageNextPage(
          param.dynamicPackageId,
          param.searchGroupId
        );
        if (Object.keys(nextData).length === 0) {
          dpPreReservationEvent();
          router.push(
            `/dynamic_package_reservations/new?dynamic_package_id=${param.dynamicPackageId}&cache_id=${data.cacheId}`
          );
        } else {
          router.push(`/hotels/?${stringify(
            decamelizeKeys(nextData.conditions),
            { arrayFormat: "indices" }
          )}
&cache_id=${nextData.cacheId}
&dynamic_package_id=${nextData.dynamicPackageId}&search_group_id=${
            nextData.searchGroupId
          }&search_type=dp`);
        }
      } else {
        router.push(
          `/hotel_reservations/new?cache_id=${data.cacheId}&hotel_id=${hotelId}&room_cache_id=${param.roomCacheId}`
        );
      }
      stopBodyScrolling(false);
    } catch (e) {
      console.log(e);
      setIsSubmitting(false);
      setErrorText(e.errorMessages);
    }
  };

  const renderOption = (): JSX.Element | null => {
    if (
      hotelPlan.isRefundable ||
      (hotelPlan.availableRoomNum > 0 &&
        hotelPlan.availableRoomNum < showRoomNumCount)
    ) {
      return (
        <div css={optionBlockStyle}>
          {hotelPlan.availableRoomNum < showRoomNumCount ? (
            <p css={roomsLeftStyle}>
              <span>
                {t("common.roomsLeft", { count: hotelPlan.availableRoomNum })}
              </span>
            </p>
          ) : (
            ""
          )}
          {hotelPlan.isRefundable ? (
            <p css={optionStyle}>
              <span>{t("common.cancelFree")}</span>
            </p>
          ) : (
            ""
          )}
        </div>
      );
    }
    return null;
  };

  const renderAmenities = (): JSX.Element[] => {
    return hotelPlan.hotelPlanAmenities.map(
      (amenity: Amenity, index: number): JSX.Element => {
        return renderAmenity(amenity, index);
      }
    );
  };

  const renderAmenity = (amenity: Amenity, index: number): JSX.Element => {
    return <div key={index}>{amenity.name}</div>;
  };

  const renderPrice = (): JSX.Element => {
    if (param.hotelPlanId || param.lId) {
      return <p css={priceStyle}>{hotelPlan.totalPriceDifference}</p>;
    } else {
      return <p css={priceStyle}>{hotelPlan.inclusiveTotalPrice}</p>;
    }
  };

  const renderButton = (): JSX.Element | null => {
    if (param.lId) {
      if (
        loggedInSocialMedia ||
        (currentUser && currentUser.chatPlatformType === PlatformTypes.email)
      ) {
        return (
          <>
            <ButtonWithLoader
              type="submit"
              buttonType="buttonSmall"
              styles={{ exBlockStyle: reserveBtnBlockStyle }}
              onClick={onClickSelection}
              isLoading={isSubmitting}
            >
              {t("common.select")}
            </ButtonWithLoader>
            {errorText && <div css={errorTextStyle}>{errorText}</div>}
          </>
        );
      } else {
        return null;
      }
    } else {
      return (
        <ButtonWithLoader
          type="submit"
          buttonType="buttonSmall"
          styles={{ exBlockStyle: reserveBtnBlockStyle }}
          onClick={onClickPreReservation}
          isLoading={isSubmitting}
        >
          {t("common.select")}
        </ButtonWithLoader>
      );
    }
  };

  return (
    <div css={planItemStyle}>
      {renderOption()}
      <div css={reserveBlockStyle}>
        <div css={amenityBlockStyle}>{renderAmenities()}</div>
        <div css={priceBlockStyle}>
          <div css={priceBlockInnerStyle}>
            {hotelPlan.hasExtraFee && (
              <div css={noteTextStyle}>
                {t("component:hotelIndex.extraFeeNotice")}
              </div>
            )}
            {renderPrice()}
            {renderButton()}
          </div>
        </div>
      </div>
    </div>
  );
};

const planItemStyle = css`
  border-top: 1px solid ${borderColor.primaryLightColor};
  padding: ${space.atom};
  &:first-of-type {
    border-top: 0;
  }
  ${moreThanBreakpoint("tablet")} {
    flex: 1;
  }
`;
const optionBlockStyle = css`
  display: flex;
  padding: ${space.atom};
`;
const optionStyle = css`
  margin: 0;
  font-size: ${fontSize.caption};
  line-height: ${lineHeight.label};
  color: ${textColor.primaryDarkColor};
  background-color: ${backgroundColor.secondaryLightColor};
  font-weight: ${fontWeight.bold};
  padding: ${space.atom0_5x} ${space.atom1_5x} 0.2rem;
  box-sizing: border-box;
  display: inline-block;
`;
const roomsLeftStyle = css`
  margin: 0;
  color: #fff;
  font-size: ${fontSize.caption};
  line-height: ${lineHeight.label};
  background-color: ${accentColor.secondaryColor};
  padding: ${space.atom0_5x} ${space.atom1_5x} 0.2rem;
  display: inline-block;
  text-align: center;
  box-sizing: border-box;
`;
const priceBlockStyle = css`
  margin-left: auto;
  box-sizing: border-box;
  padding: ${space.atom};
  ${moreThanBreakpoint("desktop")} {
    width: 60%;
  },
`;
const priceBlockInnerStyle = css`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  box-sizing: border-box;
`;
const priceStyle = css`
  color: ${textColor.primaryDarkColor};
  font-size: ${fontSize.headline};
  font-weight: ${fontWeight.bold};
  margin: 0;
  flex: 1;
  text-align: right;
  font-variant-numeric: tabular-nums;
`;
const reserveBlockStyle = css`
  position: relative;
  display: flex;
  flex-wrap: wrap;
`;
const reserveBtnBlockStyle = css`
  width: 114px;
  max-width: 100%;
  padding: 0 0 0 ${space.atom2x};
`;
const amenityBlockStyle = css`
  padding: ${space.atom};
  font-size: ${fontSize.caption};
  width: 100%;
  color: ${textColor.secondaryDarkColor};
  box-sizing: border-box;
  ${moreThanBreakpoint("desktop")} {
    width: 40%;
  }
`;
const noteTextStyle = css`
  width: 100%;
  text-align: right;
  font-size: ${fontSize.smallest};
  line-height: ${lineHeight.caption3};
  color: rgba(45, 45, 45, 0.5);
  margin: 0.15rem 0 0.5rem auto;
`;
const errorTextStyle = css`
  width: 100%;
  padding-top: ${space.atom0_5x};
  font-size: ${fontSize.caption};
  line-height: ${lineHeight.caption3};
  text-align: right;
  color: ${accentColor.alertColor};
`;
